/**
 * Scene-level scroll-driven animation (single scene container transform).
 * Used when data.animation defines position + scrollTrigger for the whole scene.
 */

import { areAnimationsEnabled } from '../../../utils/animation-settings'
import type { SceneData } from '../types'

export interface SceneAnimationInstance {
  kill: () => void
  __cleanup: () => void
}

/**
 * Sets up scroll-driven transform animation on the scene container.
 * Returns a killable instance or null if no animation config.
 */
export function setupSceneAnimation(container: HTMLDivElement, data: SceneData): SceneAnimationInstance | null {
  const animationConfig = data.animation
  if (!animationConfig) return null

  const position = animationConfig.position
  const hasLeft = position.left !== undefined
  const hasTop = position.top !== undefined
  if (!hasLeft && !hasTop) return null

  const fromX = hasLeft ? position.left!.from : 0
  const deltaX = hasLeft ? position.left!.to - position.left!.from : 0
  const fromY = hasTop ? position.top!.from : 0
  const deltaY = hasTop ? position.top!.to - position.top!.from : 0

  container.style.willChange = 'transform'

  const setTransform = (xDvw: number, yDvw: number) => {
    container.style.transform = `translate3d(${xDvw}dvw,${yDvw}dvw,0)`
  }

  if (!areAnimationsEnabled()) {
    setTransform(fromX, fromY)
    const noop = () => {}
    return { kill: noop, __cleanup: noop }
  }

  const scrollTrigger = animationConfig.scrollTrigger
  const startConfig = scrollTrigger.start
  const useViewportMode = typeof startConfig === 'object' && startConfig !== null

  const vpFraction = useViewportMode
    ? (startConfig as { viewport: number }).viewport / 100
    : (startConfig as number) / 100
  const distFraction = scrollTrigger.distance / 100
  const elemFraction = useViewportMode ? (startConfig as { element: number }).element / 100 : 0.5

  const updateAnimation = () => {
    const vh = window.innerHeight
    const startVP = vpFraction * vh
    const endVP = startVP - distFraction * vh

    const rect = container.getBoundingClientRect()
    const pivotViewport = rect.top + rect.height * elemFraction

    let progress: number
    if (pivotViewport >= startVP) progress = 0
    else if (pivotViewport <= endVP) progress = 1
    else progress = (startVP - pivotViewport) / (startVP - endVP)

    setTransform(fromX + deltaX * progress, fromY + deltaY * progress)
  }

  let rafId: number | null = null
  const scrollHandler = () => {
    if (rafId === null) {
      rafId = requestAnimationFrame(() => {
        updateAnimation()
        rafId = null
      })
    }
  }
  const resizeHandler = () => {
    if (rafId === null) {
      rafId = requestAnimationFrame(() => {
        updateAnimation()
        rafId = null
      })
    }
  }

  const cleanup = () => {
    if (rafId !== null) {
      cancelAnimationFrame(rafId)
      rafId = null
    }
    window.removeEventListener('scroll', scrollHandler)
    window.removeEventListener('resize', resizeHandler)
  }

  setTransform(fromX, fromY)
  void container.offsetHeight
  updateAnimation()
  window.addEventListener('scroll', scrollHandler, { passive: true })
  window.addEventListener('resize', resizeHandler, { passive: true })

  return { kill: cleanup, __cleanup: cleanup }
}

/**
 * Runs scene animation setup once container is in DOM. Calls onReady with the instance when done.
 *
 * If the container is NOT in the DOM yet (first call during render()), this just
 * sets the initial "from" position and returns null — no deferred timers, no polling.
 * The real animation is created by the second call (after image load) when the
 * container is in the DOM and dimensions are stable.
 */
export function runSceneAnimationWhenReady(
  container: HTMLDivElement,
  data: SceneData,
  onReady: (instance: SceneAnimationInstance | null) => void,
): void {
  if (!container.parentElement) {
    // Not in DOM — just pin at the "from" position so nothing is visible at (0,0).
    const pos = data.animation?.position
    if (pos) {
      const x = pos.left?.from ?? 0
      const y = pos.top?.from ?? 0
      container.style.transform = `translate3d(${x}dvw,${y}dvw,0)`
      container.style.willChange = 'transform'
    }
    onReady(null)
    return
  }

  // Container is in DOM — set up the real animation after a short stabilization delay.
  requestAnimationFrame(() => {
    requestAnimationFrame(() => {
      const instance = setupSceneAnimation(container, data)
      onReady(instance)
    })
  })
}
